import React, { useState, useEffect } from 'react';
import { DateTimeSelector } from './DateTimeSelector'; // Adjust the path as needed
import './ViewTest.css';

const ViewTest = ({ selectedCompany, selectedJob }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [deletingTests, setDeletingTests] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const employerEmail = "contact@newzenalpha.com"; // ✅ Replace with real email or get from auth context
  const [showReschedulePopup, setShowReschedulePopup] = useState(false);
  const [rescheduleTestId, setRescheduleTestId] = useState(null);
  const [rescheduleCandidateName, setRescheduleCandidateName] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [isRescheduling, setIsRescheduling] = useState(false);

  useEffect(() => {
    // Only fetch tests if we have a selected company and job
    if (!selectedCompany || !selectedJob) {
      setTests([]);
      setLoading(false);
      return;
    }

    // Fetch tests for the specific job
    fetch(`/api/get_job_tests/${selectedJob.job_id}`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          // Transform data to create individual cards for each candidate
          const individualTests = [];
          data.forEach(test => {
            if (test.candidate_names && test.candidate_names.length > 0) {
              // Create individual test card for each candidate
              test.candidate_names.forEach((candidateName, index) => {
                individualTests.push({
                  ...test,
                  candidate_name: candidateName,
                  candidate_email: test.selected_users ? test.selected_users.split(',')[index]?.trim() : '',
                  individual_test_id: `${test.test_id}_${index}`,
                  is_completed: index < test.total_completed // Simple logic - can be improved
                });
              });
            } else if (test.selected_users && test.selected_users.trim() !== '') {
              // If there are selected_users but no candidate_names, it's a data inconsistency
              // Split the users and create cards for each
              const user_emails = test.selected_users.split(',').map(email => email.trim()).filter(email => email);
              user_emails.forEach((email, index) => {
                individualTests.push({
                  ...test,
                  candidate_name: `Candidate ${index + 1}`, // Fallback name
                  candidate_email: email,
                  individual_test_id: `${test.test_id}_${index}`,
                  is_completed: index < test.total_completed
                });
              });
            }
            // No else clause - if no candidates and no selected_users, don't show the test
          });
          setTests(individualTests);
        } else {
          console.warn("Invalid data format:", data);
          setTests([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching tests:", err);
        setLoading(false);
      });
  }, [selectedCompany, selectedJob]);

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  // Function to get test type display name
  const getTestTypeDisplay = (testType) => {
    switch (testType) {
      case 'b':
        return 'Behavioral Test';
      case 'p':
        return 'Personality Test';
      case 't':
        return 'Screening Test';
      case 'c':
        return 'Coding Test';
      case 'a':
        return 'Personality & Behavioral Test';
      case 'o':
        return 'Communication Test';
      default:
        return 'Unknown Test Type';
    }
  };

  // Filter tests based on active tab
  const filteredTests = tests.filter(test => {
    if (activeTab === 'all') return true;
    return test.test_type === activeTab;
  });

  // Handle delete test button click
  const handleDeleteClick = (testId, candidateEmail, candidateName) => {
    console.log('Delete button clicked with:', { testId, candidateEmail, candidateName });
    setDeleteTarget({ testId, candidateEmail, candidateName });
    setShowDeleteConfirm(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    
    const { testId, candidateEmail, candidateName } = deleteTarget;
    console.log('User confirmed deletion for:', deleteTarget);
    
    const deleteKey = `${testId}_${candidateEmail}`;
    setDeletingTests(prev => new Set(prev).add(deleteKey));
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
    
    try {
      console.log('Making API call to delete test...');
      const requestBody = {
        test_id: testId,
        candidate_email: candidateEmail
      };
      console.log('Request body:', requestBody);
      
      const response = await fetch('/api/delete_test', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok) {
        console.log('Test deleted successfully, updating UI...');
        // Remove the deleted test from the state
        setTests(prevTests => {
          const filtered = prevTests.filter(test => 
            !(test.test_id === testId && test.candidate_email === candidateEmail)
          );
          console.log('Tests after filtering:', filtered.length);
          return filtered;
        });
        alert('Test deleted successfully!');
      } else {
        console.error('API error:', data);
        alert(`Error: ${data.error || 'Failed to delete test'}`);
      }
    } catch (error) {
      console.error('Error deleting test:', error);
      alert('Error deleting test. Please try again.');
    } finally {
      setDeletingTests(prev => {
        const newSet = new Set(prev);
        newSet.delete(deleteKey);
        return newSet;
      });
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    console.log('User cancelled deletion');
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const handleRescheduleTest = (individualTestId, candidateName) => {
    setRescheduleTestId(individualTestId);
    setRescheduleCandidateName(candidateName);
    setRescheduleTime('');
    setRescheduleDate('');
    setShowReschedulePopup(true);
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleTestId || !rescheduleDate || !rescheduleTime) {
      alert('Please select date and time');
      return;
    }
  
    setIsRescheduling(true);
    
    try {
      const datetimeString = `${rescheduleDate}T${rescheduleTime}:00`;
      const requestBody = {
        test_id: rescheduleTestId.split('_')[0], // Extract original test ID
        candidate_email: tests.find(t => t.individual_test_id === rescheduleTestId)?.candidate_email,
        new_scheduled_time: datetimeString
      };
  
      const response = await fetch('/api/reschedule_test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
  
      const data = await response.json();
  
      if (response.ok) {
        // Update the test in state with new time
        setTests(prevTests => 
          prevTests.map(test => 
            test.individual_test_id === rescheduleTestId
              ? { ...test, scheduled_time: datetimeString }
              : test
          )
        );
        setShowReschedulePopup(false);
        alert('Test rescheduled successfully!');
      } else {
        alert(`Error: ${data.error || 'Failed to reschedule test'}`);
      }
    } catch (error) {
      console.error('Error rescheduling test:', error);
      alert('Error rescheduling test. Please try again.');
    } finally {
      setIsRescheduling(false);
    }
  };

  // Show message if no company or job is selected
  if (!selectedCompany || !selectedJob) {
    return (
      <div className="view-test-dashboard-container">
        <h1 className="view-test-title">Test Management</h1>
        <p style={{ textAlign: 'center', marginTop: '20px', color: '#666' }}>
          Please select a company and job to view tests.
        </p>
      </div>
    );
  }

  return (
    <div className="view-test-dashboard-container">
      {/* <h1 className="view-test-title">Test Management</h1> */}
      <div className="view-test-job-info">
        <h2>{selectedJob.job_title}</h2>
        <p>{selectedCompany.name} • {selectedJob.location} • {selectedJob.experience_range}</p>
      </div>
      
      <div className="view-test-tabs">
        <span 
          className={activeTab === 'all' ? 'view-test-active-tab' : ''} 
          onClick={() => setActiveTab('all')}
        >
          All Tests
        </span>
        <span 
          className={activeTab === 'b' ? 'view-test-active-tab' : ''} 
          onClick={() => setActiveTab('b')}
        >
          Behavior Test
        </span>
        <span 
          className={activeTab === 'p' ? 'view-test-active-tab' : ''} 
          onClick={() => setActiveTab('p')}
        >
          Personality Test
        </span>
        <span 
          className={activeTab === 't' ? 'view-test-active-tab' : ''} 
          onClick={() => setActiveTab('t')}
        >
          Screening Test
        </span>
        <span 
          className={activeTab === 'c' ? 'view-test-active-tab' : ''} 
          onClick={() => setActiveTab('c')}
        >
          Coding Test
        </span>
      </div>

      <div className="view-test-top-actions">
        <button className="view-test-dropdown-btn" onClick={toggleDropdown}>
          Actions ⌄
        </button>
        {dropdownOpen && (
          <div className="view-test-dropdown-menu">
            <div>Already Completed</div>
            <div>Upcoming Test</div>
            <div>Reschedule Test</div>
          </div>
        )}
      </div>

      {loading ? (
        <p style={{ textAlign: 'center' }}>Loading tests...</p>
      ) : (
        <div className="view-test-card-grid">
          {filteredTests.map((test, idx) => (
            <div className="view-test-card" key={test.individual_test_id}>
              {/* <div className="view-test-card-header">
                <h3>{getTestTypeDisplay(test.test_type)}</h3>
                <span className="view-test-test-id">ID: {test.test_id}</span>
              </div> */}
              
              <div className="view-test-card-content">
                <div className="view-test-candidate-info">
                  <h4>{test.candidate_name}</h4>
                  {test.candidate_email && (
                    <p className="view-test-candidate-email">{test.candidate_email}</p>
                  )}
                </div>
                
                <p><strong>Topic:</strong> {test.topic}</p>
                <p><strong>Difficulty:</strong> {test.difficulty}</p>
                <p><strong>Test Type:</strong> {getTestTypeDisplay(test.test_type)}</p>
                <p><strong>Test Time:</strong> {test.scheduled_time}</p>
                <p><strong>Test id</strong> {test.test_id}</p>

                
                <div className="view-test-test-status">
                  <p><strong>Status:</strong> 
                    <span className={`view-test-status ${test.is_completed ? 'view-test-completed' : 'view-test-pending'}`}>
                      {test.is_completed ? 'Completed' : 'Pending'}
                    </span>
                  </p>
                </div>

              </div>
              
              <div className="view-test-btn-row">
                {!test.is_completed ? (
                  <>
                    <button 
                      className="view-test-btn view-test-reschedule" 
                      onClick={() => handleRescheduleTest(test.individual_test_id, test.candidate_name)}
                    >
                      Reschedule
                    </button>
                    <button 
                      className="view-test-btn view-test-delete" 
                      onClick={() => handleDeleteClick(test.test_id, test.candidate_email, test.candidate_name)}
                      disabled={deletingTests.has(`${test.test_id}_${test.candidate_email}`)}
                    >
                      {deletingTests.has(`${test.test_id}_${test.candidate_email}`) ? 'Deleting...' : 'Delete Test'}
                    </button>
                  </>
                ) : (
                  <>
                    <button className="view-test-btn view-test-result">View Result</button>
                    <button 
                      className="view-test-btn view-test-delete" 
                      onClick={() => handleDeleteClick(test.test_id, test.candidate_email, test.candidate_name)}
                      disabled={deletingTests.has(`${test.test_id}_${test.candidate_email}`)}
                    >
                      {deletingTests.has(`${test.test_id}_${test.candidate_email}`) ? 'Deleting...' : 'Delete Test'}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {!loading && filteredTests.length === 0 && (
        <p style={{ textAlign: 'center', marginTop: '20px' }}>
          No tests found for the selected job.
        </p>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirm && deleteTarget && (
        <div className="view-test-delete-confirm-overlay">
          <div className="view-test-delete-confirm-popup">
            <h3>Confirm Delete Test</h3>
            <div className="view-test-delete-details">
              <p><strong>Candidate:</strong> {deleteTarget.candidateName}</p>
              <p><strong>Test ID:</strong> {deleteTarget.testId}</p>
              <p><strong>Email:</strong> {deleteTarget.candidateEmail}</p>
            </div>
            <p className="view-test-delete-warning">
              Are you sure you want to delete this test? This action cannot be undone.
            </p>
            <div className="view-test-delete-actions">
              <button 
                className="view-test-btn view-test-cancel" 
                onClick={handleDeleteCancel}
              >
                Cancel
              </button>
              <button 
                className="view-test-btn view-test-delete-confirm" 
                onClick={handleDeleteConfirm}
              >
                Delete Test
              </button>
            </div>
          </div>
        </div>
      )}
      {showReschedulePopup && (
        <div className="view-test-reschedule-overlay">
          <div className="view-test-reschedule-popup">
            <div className="view-test-reschedule-header">
              <h3>Reschedule Test</h3>
              <button 
                className="view-test-reschedule-close"
                onClick={() => setShowReschedulePopup(false)}
              >
                &times;
              </button>
            </div>
            
            <div className="view-test-reschedule-content">
              <p className="view-test-reschedule-candidate">
                <strong>Candidate:</strong> {rescheduleCandidateName}
              </p>
              
              <div className="view-test-reschedule-form">
                <div className="view-test-reschedule-field">
                  <label>New Date & Time</label>
                  <DateTimeSelector 
                    value={rescheduleDate && rescheduleTime ? `${rescheduleDate}T${rescheduleTime}` : ''}
                    onChange={(datetime) => {
                      const [date, time] = datetime.split('T');
                      setRescheduleDate(date);
                      setRescheduleTime(time);
                    }}
                  />
                </div>
              </div>
            </div>
            
            <div className="view-test-reschedule-actions">
              <button
                className="view-test-btn view-test-reschedule-cancel"
                onClick={() => setShowReschedulePopup(false)}
                disabled={isRescheduling}
              >
                Cancel
              </button>
              <button
                className="view-test-btn view-test-reschedule-confirm"
                onClick={handleRescheduleSubmit}
                disabled={isRescheduling || !rescheduleDate || !rescheduleTime}
              >
                {isRescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewTest;

